import { useEffect, useState } from 'react';

import { useLocation } from 'react-router';

import {
  clearVerificationCookies,
  getStickyVerifyCustomerId,
  getVerifiedCustomerId,
  setStickyVerifyCustomerId,
  setVerifiedCustomerId,
} from '~/utils/verificationCookies';

export const useStickyVerifyCustomer = () => {
  const location = useLocation();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);

  // Check URL parameters and cookies on mount and location change
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlCustomerId = urlParams.get('stickyVerifyCustomer');
    const verifiedParam = urlParams.get('verified');
    const cookieCustomerId = getStickyVerifyCustomerId();
    const verifiedCookieCustomerId = getVerifiedCustomerId();

    // URL parameters always take precedence over cookies
    if (urlCustomerId) {
      // If both params are in URL, set customerVerified cookie
      if (verifiedParam === 'true') {
        setCustomerId(urlCustomerId);
        setIsVerified(true);
        setVerifiedCustomerId(urlCustomerId);
        // Clear stickyVerifyCustomer cookie if it exists
        setStickyVerifyCustomerId(null);
        return;
      }

      // Check if URL customer matches verified cookie
      if (verifiedCookieCustomerId && String(verifiedCookieCustomerId) === String(urlCustomerId)) {
        // URL matches verified cookie - use verified state
        setCustomerId(urlCustomerId);
        setIsVerified(true);
        return;
      }

      // URL customer doesn't match verified cookie (or no verified cookie) - use URL, clear verified cookie
      setCustomerId(urlCustomerId);
      setIsVerified(false);
      setStickyVerifyCustomerId(urlCustomerId);
      // Clear verified cookie if switching to different customer
      if (verifiedCookieCustomerId && String(verifiedCookieCustomerId) !== String(urlCustomerId)) {
        setVerifiedCustomerId(null);
      }
      return;
    }

    // No URL parameter - fall back to cookies
    // Check verified cookie first (takes precedence over sticky verify cookie)
    if (verifiedCookieCustomerId) {
      setCustomerId(verifiedCookieCustomerId);
      setIsVerified(true);
      return;
    }

    // Fall back to sticky verify cookie
    if (cookieCustomerId) {
      setCustomerId(cookieCustomerId);
      setIsVerified(false);
    } else {
      // No cookies or URL params
      setCustomerId(null);
      setIsVerified(false);
    }
  }, [location.search]);

  const closeStickyVerify = () => {
    setCustomerId(null);
    setIsVerified(false);
    clearVerificationCookies();
  };

  return {
    customerId,
    isVerified,
    closeStickyVerify,
  };
};
